
import Vue from 'vue';
import { store } from '@/store/index';
import {
  dispatchGetDynamicListTemplate,
  dispatchCreateDynamicListFromTemplate,
} from '@/store/main/actionsMain';
import { commitAddNotification } from '@/store/main/mutations';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import { usePermissionComposable } from '@/composables/usePermission';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { regulationDatabaseUrl } from '@/env';
import {
  buildTagItems,
  buildDbtTypeLabels,
  checkHasThreshold,
  parseLawName,
  buildSingleLawRegulationLinks,
  COLORS,
} from './templateUtils';
import ExtractionConditionsPanel from '@/views/pages/dynamicList/ExtractionConditionsPanel.vue';
import {
  hasRuleTreeConditions,
  ExtractionConditions,
} from '@/views/pages/dynamicList/extractionConditions';

const m = defineMessages({
  regulationDatabase: '法令データベース',
  noDataLoaded: 'データを読み込めませんでした',
  successAddedChecklist: '{name}を追加しました',
  thresholdTooltip:
    '法令に定められた閾値（裾切値）を考慮してチェックを行います。\n閾値未満（法令によっては閾値以下）の含有量は対象外として扱われます。',
  alreadyApplied: 'このチェックリストはすでに追加済みです',
});

interface Label {
  id: number;
  name: string;
  type: string;
}

interface Law {
  id: number;
  name: string;
}

interface Regulation {
  id: number;
  name: string;
}

interface Link {
  id: number;
  name: string;
  url: string;
  type: string;
}

interface Template {
  id: number;
  name: string;
  overview?: string;
  template_type: string;
  dbt_types: string[] | null;
  labels: Label[];
  laws: Law[];
  regulations: Regulation[];
  related_links: Link[];
  is_applied?: boolean;
  /** ノーコード（rule_tree）のテンプレだけ返る。無ければ従来の dbt_types 表示に落とす */
  extraction_conditions?: ExtractionConditions | null;
}

interface CreateListPayload {
  template_id: number;
  name_override?: string;
  description_override?: string;
}

export default Vue.extend({
  name: 'DynamicListTemplateDetailPage',
  components: {
    ExtractionConditionsPanel,
  },
  setup() {
    const { isFeatureActive, COMPANY_FEATURES, generateInactiveMessage } =
      useCompanyFeatureComposable();
    const { hasPermission, PERMISSIONS: P } = usePermissionComposable();
    return {
      m,
      sharedMessages,
      parseLawName,
      isFeatureActive,
      COMPANY_FEATURES,
      generateInactiveMessage,
      hasPermission,
      P,
      COLORS,
    };
  },
  data() {
    return {
      loading: false,
      creating: false,
      template: null as Template | null,
      editingName: false,
      editingOverview: false,
      editedName: '',
      editedOverview: '',
      customizedName: null as string | null,
      customizedOverview: null as string | null,
    };
  },
  computed: {
    templateId(): number {
      return parseInt(this.$route.params.templateId, 10);
    },
    isApplied(): boolean {
      return !!this.template?.is_applied;
    },
    canCreateList(): boolean {
      if (!this.template) return false;
      if (this.isApplied) return false;
      if (!this.hasPermission(this.P.CHECK_LIST_REQUEST)) return false;
      if (this.template.template_type === 'paid') {
        return this.isFeatureActive(this.COMPANY_FEATURES.CREATE_CHECKLIST);
      }
      return true;
    },
    allTags() {
      if (!this.template) return [];
      return buildTagItems(this.template);
    },
    // ノーコード（rule_tree）テンプレは、BEが組み立てた抽出条件が返る。
    // 返らないテンプレは従来どおり laws / regulations / dbt_types で表示する
    ruleTreeConditions(): unknown {
      if (!this.template) return null;
      return this.template.extraction_conditions || null;
    },
    hasRuleTree(): boolean {
      return hasRuleTreeConditions(this.ruleTreeConditions as never);
    },
    lawNames(): string[] {
      if (!this.template || !this.template.laws) return [];
      return this.template.laws.map((law) => law.name);
    },
    regulationNames(): string[] {
      if (!this.template || !this.template.regulations) return [];
      return this.template.regulations.map((reg) => reg.name);
    },
    dbtTypeLabels(): string[] {
      if (!this.template || !this.template.dbt_types) return [];
      return buildDbtTypeLabels(this.template.dbt_types, {
        cas: this.$t(sharedMessages.dbtTypeCas) as string,
        material: this.$t(sharedMessages.dbtTypeMaterial) as string,
        regulation: this.$t(sharedMessages.dbtTypeRegulation) as string,
      });
    },
    hasThreshold(): boolean {
      if (!this.template || !this.template.dbt_types) return false;
      return checkHasThreshold(this.template.dbt_types);
    },
    regulationLinks(): { name: string; url: string }[] {
      if (!this.template || !this.template.regulations) return [];

      return buildSingleLawRegulationLinks(
        this.template.laws?.[0],
        this.template.regulations,
        regulationDatabaseUrl
      );
    },
    knowledgeCenterLinks(): { name: string; url: string }[] {
      if (!this.template || !this.template.related_links) return [];
      return this.template.related_links
        .filter((link) => link.type === 'knowledge_center')
        .map((link) => ({
          name: link.name,
          url: link.url,
        }));
    },
    relatedLinks(): { name: string; url: string }[] {
      if (!this.template || !this.template.related_links) return [];
      return this.template.related_links
        .filter((link) => link.type === 'related')
        .map((link) => ({
          name: link.name,
          url: link.url,
        }));
    },
  },
  async mounted() {
    await this.loadTemplate();
  },
  methods: {
    async loadTemplate() {
      this.loading = true;
      try {
        const response = await dispatchGetDynamicListTemplate(store, {
          templateId: this.templateId,
        });
        this.template = response?.data || response;
      } catch (error) {
        // Error notification is already handled by dispatchCheckApiError
      } finally {
        this.loading = false;
      }
    },
    async createList() {
      this.creating = true;
      try {
        const payload: CreateListPayload = {
          template_id: this.templateId,
        };

        if (this.customizedName) {
          payload.name_override = this.customizedName;
        }

        if (this.customizedOverview) {
          payload.description_override = this.customizedOverview;
        }

        await dispatchCreateDynamicListFromTemplate(store, payload);

        const checklistName = this.customizedName || this.template?.name;
        commitAddNotification(store, {
          messageKey: m.successAddedChecklist,
          messageValues: { name: checklistName || '' },
          color: 'success',
        });

        this.$router.push({
          name: 'sdsDynamicList',
        });
      } catch {
        // Error notification is handled by dispatchCheckApiError in the action
      } finally {
        this.creating = false;
      }
    },
    goBack() {
      this.$router.push({ name: 'sdsDynamicListTemplates' });
    },
    startEditName() {
      this.editedName = this.customizedName || this.template?.name || '';
      this.editingName = true;
    },
    saveName() {
      this.customizedName = this.editedName;
      if (this.template) {
        this.template = {
          ...this.template,
          name: this.editedName,
        };
      }
      this.editingName = false;
    },
    cancelEditName() {
      this.editingName = false;
      this.editedName = '';
    },
    startEditOverview() {
      this.editedOverview =
        this.customizedOverview || this.template?.overview || '';
      this.editingOverview = true;
    },
    saveOverview() {
      this.customizedOverview = this.editedOverview;
      if (this.template) {
        this.template = {
          ...this.template,
          overview: this.editedOverview,
        };
      }
      this.editingOverview = false;
    },
    cancelEditOverview() {
      this.editingOverview = false;
      this.editedOverview = '';
    },
  },
});
